<script setup>
import { useStore } from "vuex";
import { computed } from "vue";
import Placement from "./placementComponents/Placement.vue";
import { MODES, SURFACES } from "./misc/constants";

const store = useStore();
const config = computed(() => store.getters.config);
const isPreview = computed(() => store.getters["app/mode"] === MODES.BLANKS);
const supportsMade = computed(() => store.getters["app/supportsMade"]);
const hideCreation = computed(() => config.value.experiments?.ab_dtf_hide_your_creation === 1);
</script>

<template>
  <Teleport :disabled="!isPreview" to="#js-product-details-made-placeholder">
    <div v-if="!supportsMade" class="product-details-print-warning" data-testid="warning-message">
      <span class="product-details-print-warning__icon">!</span>
      <span class="product-details-print-warning__text">
        This product color is not supported in Jiffy Made.
      </span>
    </div>

    <div class="product-details-print">
      <h3 v-if="config.made_dedicated_pdp" class="product-details-print__title">
        Upload your images
      </h3>
      <div
        class="product-details-print__grid-wrapper"
        :class="{ 'product-details-print__grid-wrapper--two-columns': isPreview }"
        data-testid="placement-grid"
      >
        <div class="product-details-print__placement">
          <Placement
            title-prefix="Front"
            side-key="front"
            :is-preview="isPreview"
            :hide-creation="hideCreation"
            :available-placements="[SURFACES.FRONT, SURFACES.FRONT_LEFT_CHEST]"
          />
        </div>

        <slot name="after-front"></slot>

        <div
          class="product-details-print__placement product-details-print__placement--back js-pdp-back-artwork-placement"
        >
          <Placement
            title-prefix="Back"
            side-key="back"
            :is-preview="isPreview"
            :hide-creation="hideCreation"
            :available-placements="[SURFACES.BACK]"
          />
        </div>

        <slot name="after-back"></slot>
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/mixin";
@import "frontend/init/pod/settings";

.product-details-print {
  $this: &;

  container: container / inline-size;
  position: relative;

  &__grid-wrapper {
    &--two-columns {
      @include with-container(container, $pod-pdp-desktop-response) {
        display: grid;
        gap: 38px;
        grid-template-columns: calc(50% - 19px) calc(50% - 19px);

        #{$this}__placement--back {
          margin-top: 0;
        }
      }
    }
  }

  &__title {
    margin: 0 0 15px;
    padding: 6px 16px;
    border-radius: 4px;
    background: linear-gradient(90deg, $link-water-3 6%, $white 121%);
    color: $gray-text;
    font-size: 20px;
  }

  &__placement {
    &--back {
      margin-top: 20px;

      @include with-container(container, $pod-pdp-desktop-response) {
        margin-top: 28px;
      }
    }
  }
}

.product-details-print-warning {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  padding: 6px 12px;
  border: 1px solid $warning-yellow;
  border-radius: 5px;
  background-color: $yellow-alert-background;

  &__icon {
    width: 22px;
    height: 22px;
    margin-right: 4px;
    border: 2px solid currentColor;
    border-radius: 50%;
    color: $warning-yellow;
    font-size: 14px;
    font-weight: 700;
    line-height: 18px;
    text-align: center;
  }

  &__text {
    color: $black;
    font-size: 13px;
  }
}
</style>
